import type { Schemes } from "@ext/markdown/core/Parser/Parser";
import { createToken } from "@ext/markdown/core/render/logic/Markdoc/src/tokenizer/plugins/annotations";
import type { StateBlock } from "@ext/markdown/core/render/logic/Markdoc/src/types";
import type MarkdownIt from "markdown-it/lib";
import type { PluginWithOptions } from "markdown-it/lib";
import type { RuleBlock } from "markdown-it/lib/parser_block";

/**
 * markdown-it reads one block tag per line, and the standard format packs several into one in three
 * places: `<table><colgroup>…</colgroup>`, a row of inline cells, and one inline cell of an expanded row.
 */

const ROW_OPEN = /^<tr(\s[^>]*?)?>/;
const CELL_OPEN = /^<(t[dh])((?:\s[^>]*?)?)>/;
const ROW_CLOSE = "</tr>";

const TABLE_OPEN = /^<table(\s[^>]*?)?>/;
const COLGROUP_OPEN = "<colgroup>";
const COLGROUP_CLOSE = "</colgroup>";
const COL = /^<col((?:\s[^>]*?)?)\s*\/>/;

type CellPlan = {
	/** The source of the opening tag without the angle brackets, e.g. `td colspan="2"`. */
	source: string;
	tag: string;
	openLine: number;
	closeLine: number;
	contentStart: number;
	contentEnd: number;
};

/**
 * The position of `closeTag` in `[from, to)`, skipping inline code spans. A cell whose text shows
 * `` `</td>` `` ends where its own tag ends, not where the example inside it does.
 */
const findCloseTag = (source: string, closeTag: string, from: number, to: number) => {
	let position = from;

	while (position < to) {
		if (source[position] === "\\") {
			position += 2;
			continue;
		}

		if (source[position] === "`") {
			const openStart = position;
			while (position < to && source[position] === "`") position++;
			const marker = position - openStart;

			let cursor = position;
			while (cursor < to) {
				if (source[cursor] !== "`") {
					cursor++;
					continue;
				}
				const runStart = cursor;
				while (cursor < to && source[cursor] === "`") cursor++;
				if (cursor - runStart === marker) break;
			}
			// A run of backticks that never closes is text, so the scan carries on right after it.
			if (cursor < to) position = cursor;
			continue;
		}

		if (source.startsWith(closeTag, position)) return position;
		position++;
	}

	return -1;
};

/**
 * Whether the cell being read stands inside a row. The same line written in prose — `<td>note</td>`
 * as an example of markup — is text, and reading it as a cell would leave the article growing a
 * pair of empty cells on every save.
 */
const insideRow = (state: StateBlock) => {
	const previous = state.tokens.at(-1);
	const tag = previous?.meta?.tag;
	if (previous?.nesting === 1) return tag === "tr";
	if (previous?.nesting === -1) return tag === "td" || tag === "th";
	return false;
};

const pushTag = (state: StateBlock, tags: Schemes["tags"], source: string, line: number) => {
	const token = createToken(state, source, 0, { tags });
	if (token) token.map = [line, line + 1];
};

const pushInline = (state: StateBlock, content: string, line: number) => {
	state.push("paragraph_open", "p", 1);
	const inline = state.push("inline", "", 0);
	inline.content = content;
	inline.map = [line, line + 1];
	inline.children = [];
	state.push("paragraph_close", "p", -1);
};

const findRowEnd = (state: StateBlock, startLine: number, endLine: number) => {
	for (let line = startLine; line < endLine; line++) {
		const text = state.src.slice(state.bMarks[line], state.eMarks[line]).trimEnd();
		if (text.endsWith(ROW_CLOSE)) return line;
	}
	return -1;
};

// Null leaves a row this rule does not understand to the generic tag rule rather than half-tokenized.
const planCells = (source: string, from: number, to: number, firstLine: number): CellPlan[] => {
	const cells: CellPlan[] = [];
	let position = from;
	let line = firstLine;

	const skipSpaces = () => {
		while (position < to && /\s/.test(source[position])) {
			if (source[position] === "\n") line++;
			position++;
		}
	};

	const advanceTo = (target: number) => {
		for (; position < target; position++) if (source[position] === "\n") line++;
	};

	while (position < to) {
		skipSpaces();
		if (source.startsWith(ROW_CLOSE, position)) return cells;

		const open = CELL_OPEN.exec(source.slice(position, to));
		if (!open) return null;

		const openLine = line;
		advanceTo(position + open[0].length);

		const contentStart = position;
		const closeTag = `</${open[1]}>`;
		const contentEnd = findCloseTag(source, closeTag, contentStart, to);
		if (contentEnd === -1) return null;

		advanceTo(contentEnd);
		cells.push({
			source: `${open[1]}${open[2]}`,
			tag: open[1],
			openLine,
			closeLine: line,
			contentStart,
			contentEnd,
		});
		advanceTo(contentEnd + closeTag.length);
	}

	return null;
};

const bodyIndent = (state: StateBlock, from: number, to: number) => {
	for (let line = from; line < to; line++) {
		if (state.src.slice(state.bMarks[line], state.eMarks[line]).trim()) return state.sCount[line];
	}
	return state.blkIndent;
};

const tableRule =
	(tags: Schemes["tags"]): RuleBlock =>
	(state, startLine, _endLine, silent) => {
		const start = state.bMarks[startLine] + state.tShift[startLine];
		const line = state.src.slice(start, state.eMarks[startLine]).trimEnd();

		const tableOpen = TABLE_OPEN.exec(line);
		if (!tableOpen) return false;

		const rest = line.slice(tableOpen[0].length);
		// A table tag alone on its line is read by the generic rule.
		if (!rest) return false;
		if (!rest.startsWith(COLGROUP_OPEN) || !rest.endsWith(COLGROUP_CLOSE)) return false;

		let cursor = rest.slice(COLGROUP_OPEN.length, rest.length - COLGROUP_CLOSE.length);
		while (cursor) {
			const column = COL.exec(cursor);
			if (!column) return false;
			cursor = cursor.slice(column[0].length);
		}
		if (silent) return true;

		const table = createToken(state, `table${tableOpen[1] ?? ""}`, 0, { tags });
		if (table) table.map = [startLine, startLine + 1];

		pushInline(state, rest, startLine);

		state.line = startLine + 1;
		return true;
	};

const rowRule =
	(tags: Schemes["tags"]): RuleBlock =>
	(state, startLine, endLine, silent) => {
		const rowStart = state.bMarks[startLine] + state.tShift[startLine];
		const firstLine = state.src.slice(rowStart, state.eMarks[startLine]);

		const rowOpen = ROW_OPEN.exec(firstLine);
		if (!rowOpen) return false;
		// A row tag alone on its line is read tag by tag by the generic rule.
		if (!firstLine.slice(rowOpen[0].length).trim()) return false;

		const rowEndLine = findRowEnd(state, startLine, endLine);
		if (rowEndLine === -1) return false;

		const rowEnd = state.eMarks[rowEndLine];
		const cells = planCells(state.src, rowStart + rowOpen[0].length, rowEnd, startLine);
		if (!cells) return false;
		if (silent) return true;

		pushTag(state, tags, `tr${rowOpen[1] ?? ""}`, startLine);

		for (const cell of cells) {
			pushTag(state, tags, cell.source, cell.openLine);

			if (cell.openLine === cell.closeLine) {
				pushInline(state, state.src.slice(cell.contentStart, cell.contentEnd).trim(), cell.openLine);
			} else {
				const oldIndent = state.blkIndent;
				const oldLineMax = state.lineMax;
				state.blkIndent = bodyIndent(state, cell.openLine + 1, cell.closeLine);
				state.lineMax = cell.closeLine;
				state.md.block.tokenize(state, cell.openLine + 1, cell.closeLine);
				state.blkIndent = oldIndent;
				state.lineMax = oldLineMax;
			}

			pushTag(state, tags, `/${cell.tag}`, cell.closeLine);
		}

		pushTag(state, tags, "/tr", rowEndLine);

		state.line = rowEndLine + 1;
		return true;
	};

const cellRule =
	(tags: Schemes["tags"]): RuleBlock =>
	(state, startLine, _endLine, silent) => {
		const start = state.bMarks[startLine] + state.tShift[startLine];
		const line = state.src.slice(start, state.eMarks[startLine]).trimEnd();

		const open = CELL_OPEN.exec(line);
		if (!open) return false;
		if (!insideRow(state)) return false;

		const closeTag = `</${open[1]}>`;
		const contentEnd = findCloseTag(line, closeTag, open[0].length, line.length);
		// A cell tag alone on its line is read by the generic rule; anything but one whole cell on
		// the line — a second cell, an unclosed tag — is left to it as well.
		if (contentEnd === -1 || contentEnd + closeTag.length !== line.length) return false;
		if (silent) return true;

		pushTag(state, tags, `${open[1]}${open[2]}`, startLine);
		pushInline(state, line.slice(open[0].length, contentEnd).trim(), startLine);
		pushTag(state, tags, `/${open[1]}`, startLine);

		state.line = startLine + 1;
		return true;
	};

const plugin: PluginWithOptions = (md: MarkdownIt, options: { tags: Schemes["tags"] }) => {
	md.block.ruler.before("paragraph", "packedTable", tableRule(options?.tags), { alt: ["paragraph"] });
	md.block.ruler.before("paragraph", "packedTableRow", rowRule(options?.tags), { alt: ["paragraph"] });
	md.block.ruler.before("paragraph", "packedTableCell", cellRule(options?.tags), { alt: ["paragraph"] });
};

export default plugin;
